import { Alert, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { updateRuntimeGroup } from './studioRuntimeSettings';
import type { StudioRuntimeSettings } from './studioApi';

type Speech = NonNullable<StudioRuntimeSettings['speech']>;
export const speechClarificationError = (value: StudioRuntimeSettings | null | undefined) => {
  const speech = value?.speech;
  if (speech?.unrecognised === true && !speech.response?.trim())
    return 'Enter an explicit response before enabling unrecognised speech handling.';
  const response = speech?.response;
  if (
    response !== undefined &&
    (!response.trim() || response.length > 1000 || /[^\P{Cc}\r\n\t]/u.test(response))
  )
    return 'The response must be nonblank, at most 1,000 characters, without unsupported control characters.';
  return '';
};
export const speechCampaignSupportError = (
  value: StudioRuntimeSettings | null | undefined,
  inbound = false,
  voicemailUnavailableReason = '',
) => {
  if (inbound && value?.speech?.redactPii === true)
    return 'Stored-text privacy masking is not supported for inbound SIP campaigns.';
  return value?.speech?.voicemail === true ? voicemailUnavailableReason : '';
};
const switches = [
  [
    'unrecognised',
    'Handle unrecognised speech',
    'Use the explicit response below for an unrecognised turn. Off keeps the saved response without sending it. Inherit keeps the native policy.',
  ],
  [
    'redactPii',
    'Mask stored personal information',
    'Masks stored text, reports and diagnostics; conversation processing and audio are unchanged. Applies to new calls.',
  ],
  [
    'waitForUser',
    'Wait for the customer',
    'Let the first customer turn start the conversation; automatic opening workflows must be off.',
  ],
  [
    'muteUntilGreeting',
    'Mute input until greeting finishes',
    'Requires a saved opening line. This cannot be combined with waiting for the customer.',
  ],
  [
    'formatting',
    'Spoken-text formatting',
    'Apply spoken-text formatting before synthesis; safety filtering remains active.',
  ],
  [
    'interruptions',
    'Customer interruptions',
    'Allow accepted customer speech to interrupt the agent.',
  ],
  [
    'smartTurn',
    'Smart turn detection',
    'Use the tuned smart-turn detector; off selects voice activity detection.',
  ],
  [
    'denoising',
    'Input denoising',
    'Override the existing noise policy. Enabling requires an available noise filter.',
  ],
] as const;
const numericFields = [
  [
    'volume',
    'Minimum input volume (RMS)',
    0,
    1,
    0.01,
    'Normalized audio level before recognition. Zero disables the floor.',
  ],
  [
    'backoff',
    'Interruption backoff (seconds)',
    0,
    10,
    0.1,
    'Cooldown after an accepted interruption. Requires VAD-owned interruptions, retained input and no local interruption classifier.',
  ],
  ['speed', 'Speech speed', 0.5, 2, 0.05, 'Provider-specific speech pace.'],
  [
    'waitTime',
    'Turn wait time (seconds)',
    1,
    10,
    0.1,
    'Minimum endpointing delay; it must fit the effective maximum endpointing delay.',
  ],
  [
    'confidence',
    'Voice detection confidence',
    0,
    1,
    0.01,
    'Silero activation probability, rather than transcript confidence.',
  ],
  [
    'trigger',
    'Trigger time (seconds)',
    0,
    5,
    0.1,
    'Minimum duration of detected speech before speech starts.',
  ],
  ['release', 'Release time (seconds)', 0, 5, 0.1, 'Minimum detected silence after speech.'],
] as const;

/** Each optional field is an explicit override; native policies are never materialized as defaults. */
export const StudioSpeechSettings = ({
  value,
  onChange,
  disabled,
  voiceProvider,
  squad = false,
  maxEndpointingDelay,
  languages,
  inbound = false,
  voicemailUnavailableReason = '',
}: {
  value: StudioRuntimeSettings | null | undefined;
  onChange: (value: StudioRuntimeSettings | null) => void;
  disabled: boolean;
  voiceProvider?: string;
  squad?: boolean;
  maxEndpointingDelay?: number | null;
  languages?: readonly string[];
  inbound?: boolean;
  voicemailUnavailableReason?: string;
}) => {
  const speech = value?.speech ?? {};
  const set = <K extends keyof Speech>(key: K, next: Speech[K] | undefined) => {
    const result = { ...speech };
    if (next === undefined) delete result[key];
    else result[key] = next;
    onChange(updateRuntimeGroup(value, 'speech', result));
  };
  const locked = disabled || squad;
  const voicemailReason =
    voicemailUnavailableReason ||
    (inbound ? 'Phone voicemail screening is supported only for outbound phone campaigns.' : '');
  const speedSupported = ['openai', '11labs'].includes(voiceProvider || '');
  const dictionary = speech.dictionary ?? [];
  const autoNumbersSupported =
    languages?.length === 1 &&
    ['en', 'eng', 'ar', 'ara', 'arb', 'de', 'deu', 'ru', 'rus'].includes(languages[0]);
  return (
    <Stack gap={2}>
      <Typography variant="h6">Speech runtime</Typography>
      <Typography variant="body2" color="text.secondary">
        Choose explicit overrides for this agent. Empty fields use the current native policy. Save
        the branch and publish it before testing the applied configuration.
      </Typography>
      {squad && (
        <Alert severity="warning">
          Speech overrides require handoff-aware runtime support before they can be published for a
          squad.
        </Alert>
      )}
      <Button
        variant="outlined"
        disabled={disabled || value?.speech === undefined}
        onClick={() => onChange(updateRuntimeGroup(value, 'speech', undefined))}
        sx={{ alignSelf: 'start' }}>
        Clear speech overrides
      </Button>
      <TextField
        select
        label="Phone voicemail screening"
        value={speech.voicemail === undefined ? 'native' : speech.voicemail ? 'on' : 'off'}
        disabled={locked}
        error={speech.voicemail === true && !!voicemailReason}
        helperText={
          voicemailReason ||
          'Screens outbound phone calls before the conversation. Requires a supported standard Deepgram transcriber, ordinary LLM, native VAD and private runtime. The server verifies canonical settings and every consuming campaign. Omission retains native legacy behavior; Off explicitly disables screening.'
        }
        onChange={(event) => {
          if (locked || (event.target.value === 'on' && voicemailReason)) return;
          set(
            'voicemail',
            event.target.value === 'native' ? undefined : event.target.value === 'on',
          );
        }}>
        <MenuItem value="native">Use native legacy policy</MenuItem>
        <MenuItem value="on" disabled={!!voicemailReason}>
          On
        </MenuItem>
        <MenuItem value="off">Off</MenuItem>
      </TextField>
      <Typography variant="body2" color="text.secondary">
        Browser voice and chat tests skip phone voicemail screening. They retain this saved phone
        setting; testing the agent does not test phone screening.
      </Typography>
      <TextField
        select
        label="Number pronunciation"
        value={speech.numberLanguage ?? ''}
        disabled={locked}
        helperText="Read standalone integers and decimals with up to six digits. Longer numbers, leading-zero/alphanumeric tokens and currency/grouped forms use native pronunciation. A standalone numeric ID is treated as a number. Auto requires one supported configured agent language; this choice does not change the conversation language."
        onChange={(event) => {
          const next = event.target.value as Speech['numberLanguage'] | '';
          if (next === 'auto' && !autoNumbersSupported) return;
          set('numberLanguage', next || undefined);
        }}>
        <MenuItem value="">Use current policy</MenuItem>
        <MenuItem value="auto" disabled={!autoNumbersSupported}>
          Auto
        </MenuItem>
        <MenuItem value="eng">English</MenuItem>
        <MenuItem value="arb">Arabic</MenuItem>
        <MenuItem value="deu">German</MenuItem>
        <MenuItem value="rus">Russian</MenuItem>
      </TextField>
      {speech.numberLanguage === 'auto' && !autoNumbersSupported && (
        <Alert severity="warning">
          Automatic number pronunciation requires one supported configured agent language. Choose an
          explicit language or use the current policy before publishing.
        </Alert>
      )}
      <TextField
        select
        label="Background sound"
        value={speech.background ?? ''}
        disabled={locked}
        helperText="Agent ambient preference. An explicit campaign audio override takes precedence. None explicitly disables ambient sound; thinking audio retains its native policy."
        onChange={(event) =>
          set(
            'background',
            event.target.value ? (event.target.value as Speech['background']) : undefined,
          )
        }>
        <MenuItem value="">Use native policy</MenuItem>
        <MenuItem value="none">None</MenuItem>
        <MenuItem value="office-ambience">Office ambience</MenuItem>
      </TextField>
      {numericFields.map(([key, label, min, max, step, help]) => {
        const unavailableSpeed = key === 'speed' && !speedSupported;
        const lower = key === 'speed' && voiceProvider === '11labs' ? 0.8 : min;
        const upper =
          key === 'speed' && voiceProvider === '11labs'
            ? 1.2
            : key === 'waitTime' && typeof maxEndpointingDelay === 'number'
              ? Math.min(max, maxEndpointingDelay)
              : max;
        const number = speech[key];
        return (
          <TextField
            key={key}
            label={label}
            type="number"
            value={number ?? ''}
            disabled={locked || unavailableSpeed}
            placeholder="Native default"
            error={number !== undefined && (number < lower || number > upper || unavailableSpeed)}
            helperText={
              unavailableSpeed
                ? 'This voice provider does not support an explicit speed override.'
                : help
            }
            slotProps={{ htmlInput: { min: lower, max: upper, step } }}
            onChange={(event) => {
              const raw = event.target.value;
              if (!raw.trim()) set(key, undefined);
              else if (Number.isFinite(Number(raw))) set(key, Number(raw));
            }}
          />
        );
      })}
      {switches.map(([key, label, help]) => (
        <TextField
          key={key}
          select
          label={label}
          helperText={
            key === 'redactPii' && inbound
              ? `${help} Inbound SIP startup does not support stored-text masking; use native/off. The server also checks other consuming campaigns.`
              : help
          }
          value={speech[key] === undefined ? 'native' : speech[key] ? 'on' : 'off'}
          disabled={locked}
          error={key === 'redactPii' && !!speechCampaignSupportError(value, inbound)}
          onChange={(event) => {
            if (locked || (key === 'redactPii' && inbound && event.target.value === 'on')) return;
            set(key, event.target.value === 'native' ? undefined : event.target.value === 'on');
          }}>
          <MenuItem value="native">Use native policy</MenuItem>
          <MenuItem value="on" disabled={key === 'redactPii' && inbound}>
            On
          </MenuItem>
          <MenuItem value="off">Off</MenuItem>
        </TextField>
      ))}
      <TextField
        label="Response to unrecognised speech"
        multiline
        minRows={3}
        value={speech.response ?? ''}
        disabled={locked}
        required={speech.unrecognised === true}
        error={!!speechClarificationError(value)}
        helperText="Enter your literal clarification text, up to 1,000 characters. Required when handling is On. No default phrase is inserted; the saved response is preserved while Off."
        slotProps={{ htmlInput: { maxLength: 1000 } }}
        onChange={(event) => set('response', event.target.value || undefined)}
      />
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack gap={2}>
          <Typography variant="subtitle2">Pronunciation dictionary</Typography>
          <Typography variant="body2" color="text.secondary">
            Literal spoken-text replacements apply across generated speech and greetings. Use unique
            nonblank words and pronunciations.
          </Typography>
          {dictionary.map((entry, index) => (
            <Stack key={index} direction={{ xs: 'column', sm: 'row' }} gap={1}>
              {(['word', 'pronunciation'] as const).map((key) => (
                <TextField
                  key={key}
                  label={`${key === 'word' ? 'Word' : 'Pronunciation'} ${index + 1}`}
                  value={entry[key]}
                  disabled={locked}
                  error={!entry[key].trim()}
                  slotProps={{ htmlInput: { maxLength: key === 'word' ? 120 : 240 } }}
                  onChange={(event) =>
                    set(
                      'dictionary',
                      dictionary.map((item, at) =>
                        at === index ? { ...item, [key]: event.target.value } : item,
                      ),
                    )
                  }
                />
              ))}
              <Button
                disabled={locked}
                aria-label={`Remove pronunciation ${index + 1}`}
                onClick={() =>
                  set(
                    'dictionary',
                    dictionary.filter((_, at) => at !== index),
                  )
                }>
                Remove
              </Button>
            </Stack>
          ))}
          <Button
            variant="outlined"
            disabled={locked || dictionary.length >= 100}
            onClick={() => set('dictionary', [...dictionary, { word: '', pronunciation: '' }])}
            sx={{ alignSelf: 'start' }}>
            Add pronunciation
          </Button>
        </Stack>
      </Paper>
    </Stack>
  );
};
